<!-- KeyValueTable.vue -->
<script setup lang="ts">
import { computed, type PropType } from 'vue'

type RowValue =
  | string
  | number
  | boolean
  | string[]
  | Record<string, unknown> // 👈 agora aceita objeto
  | null
  | undefined

export type Row = {
  label: string
  value?: RowValue
}

const props = defineProps({
  items: {
    type: Array as PropType<Row[]>,
    default: () => [],
  },
  /** Texto para boolean true */
  yesLabel: { type: String, default: 'Sim' },
  /** Texto para boolean false */
  noLabel: { type: String, default: 'Não' },
  /** Separador para array de strings */
  separator: { type: String, default: ', ' },

  // 🎛️ formatter opcional para objetos arbitrários
  objectFormatter: {
    type: Function as PropType<
      (obj: Record<string, unknown>, row: Row) => string
    >,
    default: undefined,
  },

  // “sangrar” até as bordas do Section
  fullBleed: { type: Boolean, default: false },
  // intensidade do negative margin (escala 4px do Vuetify)
  bleed: { type: Number, default: 4 },
})

/** utils */
const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null

const getStr = (
  o: Record<string, unknown>,
  key: string
): string | undefined => {
  const v = o[key]
  return typeof v === 'string' && v.trim() ? v : undefined
}

/** formatter default para objetos (inclui caso de telefone) */
function defaultObjectToString(obj: Record<string, unknown>): string {
  // Caso comum: telefone { countryCode, areaCode, number }
  const number = getStr(obj, 'number')
  const area = getStr(obj, 'areaCode')
  const cc =
    getStr(obj, 'countryCode') || getStr(obj, 'country') || getStr(obj, 'ddi')

  if (number || area || cc) {
    const parts: string[] = []
    if (cc) parts.push(`+${cc}`)
    if (area) parts.push(`(${area})`)
    if (number) parts.push(number)
    return parts.join(' ').trim() || '—'
  }

  // Genérico: "chave: valor, chave2: valor2"
  const entries = Object.entries(obj)
    .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
    .map(([k, v]) => `${k}: ${String(v)}`)
  return entries.join(', ') || '—'
}

/** Converte qualquer valor em string pronta para exibição */
function display(v: RowValue, row: Row): string {
  if (v === null || v === undefined) return '—'
  if (Array.isArray(v)) {
    const s = v
      .filter((x) => x !== null && x !== undefined && String(x).trim() !== '')
      .join(props.separator)
    return s || '—'
  }
  if (typeof v === 'boolean') return v ? props.yesLabel : props.noLabel
  if (isRecord(v)) {
    // se vier formatter custom, usa; senão, aplica o default
    return props.objectFormatter
      ? props.objectFormatter(v, row)
      : defaultObjectToString(v)
  }
  const s = String(v).trim()
  return s.length ? s : '—'
}

/** bleed em px */
const bleedPx = computed(() => `${props.bleed * 4}px`)

/** estilo do wrapper quando fullBleed = true */
const wrapStyle = computed(() =>
  props.fullBleed
    ? { marginLeft: `-${bleedPx.value}`, marginRight: `-${bleedPx.value}` }
    : undefined
)
</script>

<template>
  <div class="kv-wrap" :style="wrapStyle" style="padding-inline: 16px">
    <v-card variant="outlined" rounded="lg" class="kv-card">
      <v-table class="kv-table" density="comfortable">
        <tbody>
          <tr v-for="(r, i) in items" :key="i">
            <th class="kv-label">{{ r.label }}</th>
            <td class="kv-value">{{ display(r.value, r) }}</td>
          </tr>
        </tbody>
      </v-table>
    </v-card>
  </div>
</template>

<style scoped>
.kv-card {
  width: 100%;
  border: 1px solid #eee;
}
.kv-table :deep(.v-table__wrapper) {
  overflow: visible;
}
.kv-table :deep(table) {
  width: 100%;
  table-layout: fixed;
}
.kv-table :deep(th),
.kv-table :deep(td) {
  border-bottom: 1px solid #eee;
  padding: 14px 16px;
  vertical-align: middle;
}
.kv-label {
  width: 50%;
  font-weight: 400;
  font-size: 14px;
  color: #757575;
  background: white;
  white-space: nowrap;
}
.kv-value {
  color: #757575;
  word-break: break-word;
}
.kv-table :deep(tr:last-child) th,
.kv-table :deep(tr:last-child) td {
  border-bottom: 0;
}
</style>
